/* eslint-disable max-lines */
import { Brands } from '@delta-defense/client-environments';
import { FeatureFlagService } from '@delta-defense/feature-flag-service';
import { IFeatureFlagRepository } from '@delta-defense/feature-flag-service/service/iFeatureFlagRepository';
import { DefaultSteps } from '../configuration/default';
import { ElegantSteps } from '../configuration/elegant';
import { EmailCheckFields } from '../configuration/steps/default/emailCheck';
import { Step } from '../core/domain/module';
import { ITransaction } from '../core/domain/transaction/ITransaction';
import { Transaction } from '../core/domain/transaction/transaction';
import { QueryParams, Routes } from '../core/enums/module';
import { SessionStorageKeys } from '../core/services/browserStorage/keys/sessionStorageKeys';
import {
	Auth,
	BrowserStorage,
	CartAbandonment,
	IAuth,
	ICartAbandonment,
	ISessionStorage,
	ISubscriptions,
	Subscriptions
} from '../core/services/module';
import { elegantEnabled } from './elegantEnabled';
import { getEmailParam } from './getEmailParam';
import { getQueryParameter } from './getQueryParameter';
import { resolveMemberBrand } from './resolveMemberBrand';
import { routeUpgradeAndProductIdPurchases } from './routeUpgradeAndProductIdPurchases';
import { shouldUseCabelasBranding } from './shouldUseCabelasBranding';

type Router = {
	push: (url: string) => void;
	replace: (url: string) => void;
	asPath: string;
};

// eslint-disable-next-line complexity
export async function navigateUserToFirstStep(
	router: Router,
	mainLocation = location,
	transaction = Transaction.Instance(),
	auth = Auth.Instance(),
	subscriptions = Subscriptions.Instance(),
	features = FeatureFlagService.Instance(),
	session = BrowserStorage.SessionInstance(),
	cartAbandonment = CartAbandonment.Instance()
): Promise<void> {
	const emailParam = getEmailParam(mainLocation.search);
	const alreadyLoggedIn = !!auth.accessToken;
	const onIgnoredRoute = [
		'/invoice',
		Routes.SignupWelcome,
		Routes.SingleClickPurchase,
		Routes.SSO,
		Routes.PostTransactionUpdatePassword,
		Routes.PostTransactionReferral,
		Routes.CartAbandonment,
		Routes.ProductPTU,
		Routes.NewMemberIntakeSurvey
	].some((r) => mainLocation.pathname.includes(r));
	const elegantCheckoutEnabled = elegantEnabled(router.asPath);

	if (elegantCheckoutEnabled) {
		if (!onIgnoredRoute) {
			if (emailParam) {
				transaction.Data.email = emailParam;
				transaction.Steps[ElegantSteps.Elegant]?.Fields[EmailCheckFields.Email].SetValue(emailParam);
			}
			router.push(`${Routes.Elegant}${mainLocation.search}`);
		}
		return;
	} else {
		if (mainLocation.pathname === Routes.CartAbandonment) {
			await restoreAbandonedCart(alreadyLoggedIn, cartAbandonment, auth, router, mainLocation);
			return;
		}

		if (alreadyLoggedIn && !onIgnoredRoute && resumeSessionTransaction(router, session, mainLocation, transaction)) {
			return;
		}

		const emailCheckStep = transaction.Steps[DefaultSteps.EmailCheck];

		if (emailParam) {
			transaction.Data.email = emailParam;
			emailCheckStep.Fields[EmailCheckFields.Email].SetValue(emailParam);
		}

		if (!alreadyLoggedIn && !onIgnoredRoute) {
			routeUnAuthedUser(emailCheckStep, router, mainLocation, features, emailParam);
			return;
		} else if (alreadyLoggedIn && !onIgnoredRoute) {
			await routeAuthedUser(auth, subscriptions, transaction, router, mainLocation, features, session, emailParam);
		}

		emailCheckStep && (emailCheckStep.Completed = true);
	}
}

const restoreAbandonedCart = async (
	alreadyLoggedIn: boolean,
	cartAbandonment: ICartAbandonment,
	auth: IAuth,
	router: Router,
	mainLocation: Location
) => {
	cartAbandonment.Enable();
	let handled = false;
	if (alreadyLoggedIn) {
		handled = await cartAbandonment.RestoreAbandonedCart(auth.accessToken);
	}
	if (!handled) {
		router.push(`${Routes.Root}${mainLocation.search}`);
		setTimeout(() => {
			navigateUserToFirstStep(router);
		});
	}
};


const resumeSessionTransaction = (
	router: { push: (url: string) => void },
	session: ISessionStorage,
	mainLocation: Location,
	transaction: ITransaction
): boolean => {
	const transactionData = session.getStringValue(SessionStorageKeys.TransactionData);
	const lastKnownRoute = session.getItem(SessionStorageKeys.LastKnownRoute);
	const routes = Object.values(Routes);
	const productId = getQueryParameter(QueryParams.ProductId, mainLocation.search);
	const canResumeWithProductId =
		(productId && lastKnownRoute?.includes(productId)) ||
		(!productId && !lastKnownRoute?.includes(QueryParams.ProductId));
	const ignoredRoutes = [
		Routes.Root,
		Routes.SignupWelcome,
		Routes.SingleClickPurchase,
		Routes.PostTransactionReferral,
		Routes.CartAbandonment,
		Routes.PostTransactionUpdatePassword
	];
	const canResumeTransaction = !!(
		transactionData &&
		lastKnownRoute &&
		canResumeWithProductId &&
		routes.filter((r) => !ignoredRoutes.includes(r)).some((r) => lastKnownRoute.startsWith(r))
	);

	if (canResumeTransaction) {
		transaction.Data = JSON.parse(transactionData);
		const currentRoute = `${mainLocation.pathname}${mainLocation.search}`;

		if (lastKnownRoute && currentRoute !== lastKnownRoute) {
			router.push(lastKnownRoute);
		}
	} else {
		session.clear([SessionStorageKeys.UserOnCartAbandonmentPath]);
	}

	return canResumeTransaction;
};

// eslint-disable-next-line complexity
async function routeAuthedUser(
	auth: IAuth,
	subscriptions: ISubscriptions,
	transaction: ITransaction,
	router: Router,
	mainLocation: Location,
	features: IFeatureFlagRepository,
	session: ISessionStorage,
	emailParam: string | null
) {
	const [userResult, customerResult] = await Promise.all([auth.getUser(), subscriptions.getCustomer()]);
	const productId = getQueryParameter(QueryParams.ProductId, mainLocation.search);

	if (!transaction.Data.email) {
		transaction.Data.email = userResult.value?.email || '';
	}
	if (emailParam && userResult.value?.email?.toLowerCase() !== transaction.Data.email.toLowerCase()) {
		auth.logout();
		navigateUserToFirstStep(router, mainLocation, transaction, auth, subscriptions, features, session);
	} else if (
		customerResult.wasSuccessful &&
		customerResult.value?.subscriptions.some((s) => s.attributes.status === 'active')
	) {
		transaction.Data.customerSubscriptions = customerResult.value.subscriptions;
		await transaction.SetProductDataById(productId);

		const expectedBrand = resolveMemberBrand(customerResult.value?.subscriptions);
		const currentBrand = shouldUseCabelasBranding() ? Brands.CabelasShield : Brands.USCCA;

		if (expectedBrand !== currentBrand) {
			router.push(`${Routes.Existing}${mainLocation.search}`);
		} else if (
			transaction.UpgradingFromLevel ||
			transaction.SpouseOnlyPurchase ||
			transaction.IsSameLevelMonthlyToAnnualUpgrade() ||
			transaction.IsSameLevelAuthorizedSideGrade()
		) {
			await routeUpgradeAndProductIdPurchases(transaction, router, mainLocation);
		} else {
			router.push(`${Routes.Existing}${mainLocation.search}`);
		}
	} else if (!!productId) {
		await routeUpgradeAndProductIdPurchases(transaction, router, mainLocation);
	} else {
		router.push(`${Routes.SignupLevel}${mainLocation.search}`);
	}
}

const routeUnAuthedUser = (
	emailCheckStep: Step<any>,
	router: Router,
	mainLocation: Location,
	features: IFeatureFlagRepository,
	emailParam: string | null
) => {
	if (emailParam) {
		emailCheckStep.Submission.Order(async (result) => {
			const accountExists = result?.value?.accountExists;
			if (accountExists) {
				router.push(`${Routes.SignupVerification}${mainLocation.search}`);
			} else if (result?.wasSuccessful) {
				router.push(`${Routes.SignupRegistration}${mainLocation.search}`);
			} else {
				router.push(`${Routes.Email}${mainLocation.search}`);
			}
		}, false);

		emailCheckStep.Submit(true);
	} else {
		router.replace(`${Routes.Email}${mainLocation.search}`);
	}
};
